import { useEffect, useState } from 'react';
import { DeepMap, FieldError, useController, useFormContext } from 'react-hook-form';

import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { getErrorForArrayField } from 'adapter/in/common/hooks/useArrayField';
import { ConfigFieldType, Fields, FieldStatus, FormFieldData, RulesRHF, TextInputFieldData } from 'adapter/in/common/form/types';

type useTextContainerProps = {
    value: TextInputFieldData;
    error: string | undefined;
    handleOnChange: (value: string) => void;
    invalid: boolean;
};

type TextError = {
    message: string;
};

const useTextContainer = (
    name: string,
    configType: ConfigFieldType,
    rules: RulesRHF = {
        required: true,
        // Right now, it stays as valid every value entered, as an empty value may be acceptable
        validate: () => true,
    },
    dataDependsOn?: string[],
    defaultValue?: TextInputFieldData
): useTextContainerProps => {
    const { control, setValue, clearErrors } = useFormContext<Fields<TextInputFieldData>>();
    const {
        field,
        fieldState: { invalid },
        formState,
    } = useController({
        name,
        rules,
        control,
        defaultValue: defaultValue || buildTextInputFieldData(FieldStatus.INITIAL_VALUE, '', ConfigFieldType.OPTIONS, ''),
    });
    const { errors } = formState;
    const { setHasChange: setWidgetMenuHasChange } = dispatchFormChangesView();
    const [text, setText] = useState<string>('');
    const [error, setError] = useState<string>();
    const fieldValue = field.value as TextInputFieldData;

    useEffect(() => {
        if (name.includes('.')) {
            const explodeName = name.split('.');
            const errorArray = getErrorForArrayField(
                errors as DeepMap<Fields<FormFieldData>, FieldError>,
                explodeName[0],
                Number(explodeName[1]),
                explodeName[2]
            );
            setError(errorArray);
        } else {
            setError(((errors[name] as unknown) as TextError)?.message);
        }
    }, [formState]);

    useEffect(() => {
        if (fieldValue && fieldValue.status !== FieldStatus.INVALID && fieldValue.status !== FieldStatus.HAS_VALUE) {
            if (dataDependsOn && dataDependsOn.length > 0) {
                if (fieldValue.status === FieldStatus.RESET) {
                    setText('');
                }
            }
            if (fieldValue.status === FieldStatus.INITIAL_VALUE || fieldValue.status === FieldStatus.RESET) {
                setText(fieldValue.value.toString());
            }
        }
    }, [fieldValue]);

    useEffect(() => {
        if (text) {
            handleLoad(text);
        }
    }, [text]);

    const handleLoad = (value: string) => {
        clearErrors();
        setValue(name, buildTextInputFieldData(getFieldStatusToLoad(value), fieldValue.getInitialValue(), configType, value));
    };

    const handleOnChange = (value: string) => {
        setWidgetMenuHasChange(true);
        handleLoad(value);
    };

    const getFieldStatusToLoad = (value: string) => {
        if (
            configType === ConfigFieldType.OPTIONS &&
            value === fieldValue.getInitialValue() &&
            fieldValue.status === FieldStatus.DEFAULT_DATA
        ) {
            return FieldStatus.DEFAULT_DATA;
        }
        return value ? FieldStatus.HAS_VALUE : FieldStatus.INVALID;
    };

    return {
        value: fieldValue,
        error,
        handleOnChange,
        invalid,
    };
};

export default useTextContainer;
